import React, { useMemo } from 'react'
import { ItemRenderProps } from '@components/UniversalAPISelector'
import { useBusinessContext } from '@context'
import { fetchDocument, fetchDocuments, IDocument } from '@query'
import UniversalAPISelector, {
  FetchItemsProps
} from '@components/UniversalAPISelector/UniversalAPISelector'
import { InputProps } from '@components/Inputs'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { FaFileInvoice } from 'react-icons/fa'
import { DocumentListItem } from '@root/pages/DocumentPage/DocumentListItem'

interface Props extends Omit<InputProps, 'value' | 'onChange'> {
  value?: number | null
  onChange: (documentId: number | null) => void
  excludeIds?: number[]
}

const PAGE_SIZE = 50

export const DocumentSelector: React.FC<Props> = ({
  excludeIds = [],
  value,
  onChange,
  ...rest
}) => {
  const { slug } = useBusinessContext()
  const { t } = useTranslation()

  const excludeKey = useMemo(() => [...excludeIds].sort((a, b) => a - b).join('-'), [excludeIds])
  const excludeSet = useMemo(() => new Set(excludeIds), [excludeKey])

  const dataToShow = (document: IDocument): string => {
    const numberLabel = document.number?.trim() || ''
    const contact = document.contact_name?.trim() || ''
    const description = document.description?.trim() || ''
    return [numberLabel, contact, description].filter(Boolean).join(' • ')
  }

  const mapResponse = (response: Awaited<ReturnType<typeof fetchDocuments>>) => ({
    ...response,
    results: response.results.filter(document => !excludeSet.has(document.id))
  })

  const getItems = (params: FetchItemsProps) =>
    fetchDocuments(
      { slug },
      { page: params.page, search: params.search, page_size: PAGE_SIZE }
    ).then(mapResponse)

  const getItemsQueryKeyFn = (params?: Record<string, unknown>) => [
    slug,
    'documents',
    'selector',
    { ...(params ?? {}), page_size: PAGE_SIZE, excludeKey }
  ]

  const renderRow = (
    item: IDocument,
    {
      isFocused,
      isSelected,
      onKeyDown,
      handleOnItemClick,
      isFirstOfType,
      isLastOfType,
      style
    }: ItemRenderProps<IDocument>
  ) => (
    <ItemWrapper
      key={`document-${item.id}`}
      onKeyDown={onKeyDown}
      onClick={() => handleOnItemClick(item)}
      style={style}
    >
      <DocumentListItem
        document={item}
        isSelected={isFocused || isSelected}
        isLastOfType={isLastOfType}
        isFirstOfType={isFirstOfType}
        preventNavigation
      />
    </ItemWrapper>
  )

  return (
    <UniversalAPISelector
      {...rest}
      value={value ?? undefined}
      onChange={onChange}
      getItem={id => fetchDocument({ slug, documentId: id })}
      getItemQueryKeyFn={id => [slug, 'documents', id]}
      getItems={getItems}
      renderRow={renderRow}
      itemSize={96}
      getDataToShow={dataToShow}
      placeholder={t('uas.documents.placeholder')}
      noDataMessage={t('uas.documents.noData')}
      dropDownHeader={t('uas.documents.select')}
      dropdownHeaderIcon={<FaFileInvoice />}
      getItemsQueryKeyFn={getItemsQueryKeyFn}
    />
  )
}

const ItemWrapper = styled.div`
  display: flex;
  width: 100%;
  border-radius: ${({ theme }) => theme.spacing.xs}rem;
  cursor: pointer;

  & > * {
    width: 100%;
    height: 100%;
  }
`
